import { fetchEventSource } from "@microsoft/fetch-event-source";
import type { ResumeRequest, SSEEvent, StreamRequest } from "./types";
import { mockResumeTaskAuthoring, mockStreamTaskAuthoring } from "./mockStream";

const AGENT_API_HOST = process.env.REACT_APP_AGENT_API_HOST || "";

export async function* streamTaskAuthoring(
  req: StreamRequest,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  if (!AGENT_API_HOST) {
    yield* mockStreamTaskAuthoring(req, signal);
    return;
  }
  yield* postSSE(`${AGENT_API_HOST}/task/authoring/stream`, req, signal);
}

export async function* resumeTaskAuthoring(
  req: ResumeRequest,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  if (!AGENT_API_HOST) {
    yield* mockResumeTaskAuthoring(req, signal);
    return;
  }
  yield* postSSE(`${AGENT_API_HOST}/task/authoring/resume`, req, signal);
}

// fetch-event-source 는 callback 기반 — AsyncGenerator 로 변환 (queue + Promise resolver)
async function* postSSE(
  url: string,
  body: unknown,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const queue: SSEEvent[] = [];
  let resolveNext: (() => void) | null = null;
  let done = false;
  let error: Error | null = null;

  const wakeup = () => {
    const r = resolveNext;
    resolveNext = null;
    r?.();
  };

  const fetchPromise = fetchEventSource(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    },
    body: JSON.stringify(body),
    signal,
    openWhenHidden: true,
    onmessage(msg) {
      if (!msg.event) return;
      try {
        const ev = {
          event: msg.event,
          data: JSON.parse(msg.data),
        } as SSEEvent;
        queue.push(ev);
        wakeup();
      } catch {
        // malformed event — skip
      }
    },
    onerror(err) {
      error = err instanceof Error ? err : new Error(String(err));
      throw error; // 재시도 방지
    },
    onclose() {
      done = true;
      wakeup();
    },
  }).catch((e: unknown) => {
    error = e instanceof Error ? e : new Error(String(e));
    done = true;
    wakeup();
  });

  while (true) {
    while (queue.length) yield queue.shift()!;
    if (done) break;
    await new Promise<void>((r) => (resolveNext = r));
  }
  await fetchPromise;
  if (error) throw error;
}
